
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, AlertCircle, User } from 'lucide-react';
import { ExamQuestionsTable } from './ExamQuestionsTable';
import { ExamQuestionFilters } from './ExamQuestionFilters';
import { CreateExamQuestionDialog } from './CreateExamQuestionDialog';
import { TranslateCourseContentDialog } from '../courses/translation/TranslateCourseContentDialog';
import { useExamQuestions } from '@/hooks/useExamQuestions';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ExamQuestionsContentProps {
  courseId: string;
}

export function ExamQuestionsContent({ courseId }: ExamQuestionsContentProps) {
  const [filters, setFilters] = useState({
    module: '',
    part: '',
    language: 'English',
    isActive: 'all'
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  
  const { data: questions, isLoading, error, refetch, isFetching } = useExamQuestions(courseId);
  const { isAdmin, adminUser, isLoading: authLoading } = useAdminAuth();

  // Log question counts for debugging
  React.useEffect(() => {
    if (questions) {
      console.log('[ExamQuestionsContent] Total questions:', questions.length);
      console.log('[ExamQuestionsContent] English questions:', questions.filter(q => q.language === 'English').length);
      console.log('[ExamQuestionsContent] Spanish questions:', questions.filter(q => q.language === 'Spanish').length);
    }
  }, [questions]);

  // Early return for invalid courseId
  if (!courseId) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>No course selected</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Show authentication status while loading
  if (authLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mr-3"></div>
            <span>Checking authentication...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show admin status for debugging
  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <User className="h-4 w-4" />
            <AlertDescription>
              Admin access required. Current status: {isAdmin ? 'Admin' : 'Not Admin'} 
              {adminUser ? ` (User: ${adminUser.email})` : ' (Not logged in)'}
            </AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle error state with more specific messaging
  if (error) {
    console.error('Error loading exam questions:', error);
    
    const errorMessage = error?.message || 'Unknown error';
    const isAuthError = errorMessage.includes('Admin access required') || 
                       errorMessage.includes('Not authenticated');
    const isPolicyError = errorMessage.includes('security policies') ||
                         errorMessage.includes('policy');
    
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {isAuthError && 'Authentication error: Please log out and log back in.'}
              {isPolicyError && 'Database access error: Security policies are blocking access.'}
              {!isAuthError && !isPolicyError && `Error loading exam questions: ${errorMessage}`}
            </AlertDescription>
          </Alert>
          <div className="mt-4 space-y-2">
            <div className="text-sm text-muted-foreground">
              Debug info: User ID: {adminUser?.id || 'None'}, Admin: {isAdmin ? 'Yes' : 'No'}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => refetch()} variant="outline" className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
              <Button onClick={() => window.location.reload()} variant="outline">
                Refresh Page
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter questions safely
  const filteredQuestions = questions?.filter(question => {
    if (filters.module && question.module_number.toString() !== filters.module) return false;
    if (filters.part && question.part_number.toString() !== filters.part) return false;
    if (filters.language && question.language !== filters.language) return false;
    if (filters.isActive !== 'all') {
      const isActive = filters.isActive === 'true';
      if (question.is_active !== isActive) return false;
    }
    return true;
  }) || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            Exam Questions
            <span className="text-sm text-muted-foreground font-normal">
              (Total: {questions?.length || 0})
            </span>
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              onClick={() => refetch()} 
              variant="outline" 
              size="sm"
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
            <TranslateCourseContentDialog courseId={courseId} contentType="questions" />
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <ExamQuestionFilters 
              filters={filters} 
              onFiltersChange={setFilters}
              questions={questions || []}
            />
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <ExamQuestionsTable 
              questions={filteredQuestions}
              courseId={courseId}
            />
          )}
        </CardContent>
      </Card>

      <CreateExamQuestionDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        courseId={courseId}
      />
    </div>
  );
}
